import { Controller } from "@hotwired/stimulus"

// Nombre minimum d'options dans le formulaire de creation
const MIN_OPTIONS = 2
// Duree d'affichage du feedback d'erreur (ms)
const ERROR_FEEDBACK_MS = 2000

export default class extends Controller {
  static targets = ["optionsContainer", "pollList", "pollCard", "emptyState", "submitBtn", "pollBadge"]
  static values = {
    sessionId: Number,
    role: { type: String, default: "student" },
    pollsUrl: String,
    paramKey: { type: String, default: "live_session_poll" },
    labelActive: { type: String, default: "Actif" },
    labelClosed: { type: String, default: "Clôturé" },
    labelClose: { type: String, default: "Clôturer" },
    labelDeleteConfirm: { type: String, default: "Supprimer ce sondage ?" },
    labelVotes: { type: String, default: "votes" }
  }

  connect() {
    this.unvotedPollIds = new Set()
    this.initializeUnvotedPolls()
  }

  // Initialiser les sondages actifs non votes a partir du DOM
  initializeUnvotedPolls() {
    if (this.roleValue === "trainer") return

    this.pollCardTargets.forEach(card => {
      const pollId = parseInt(card.dataset.pollId)
      const hasVoteButtons = card.querySelector('button[data-action*="vote"]')
      const statusBadge = card.querySelector("[data-status-badge]")
      const isClosed = statusBadge && statusBadge.textContent.trim() === this.labelClosedValue

      if (hasVoteButtons && !isClosed) {
        this.unvotedPollIds.add(pollId)
      }
    })

    this.updatePollBadge()
  }

  // --- Evenements dispatches par live-session controller ---

  onPollLaunched(event) {
    const { poll_id, question, options } = event.detail
    this.insertNewPoll(parseInt(poll_id), question, options)
    if (this.hasEmptyStateTarget) this.emptyStateTarget.remove()

    if (this.roleValue !== "trainer") {
      this.unvotedPollIds.add(parseInt(poll_id))
      this.updatePollBadge()
    }
  }

  onPollResults(event) {
    const { poll_id, results } = event.detail
    this.updatePollResults(parseInt(poll_id), results)
  }

  onPollClosed(event) {
    const { poll_id, results } = event.detail
    const safePollId = parseInt(poll_id)
    this.markPollClosed(safePollId)
    if (results) this.updatePollResults(safePollId, results)

    this.unvotedPollIds.delete(safePollId)
    this.updatePollBadge()
  }

  onPollDeleted(event) {
    const { poll_id } = event.detail
    const safePollId = parseInt(poll_id)
    const card = this.element.querySelector(`[data-poll-id="${safePollId}"][data-live-session-poll-target="pollCard"]`)
    if (card) card.remove()

    this.unvotedPollIds.delete(safePollId)
    this.updatePollBadge()
  }

  // --- Admin: ajout d'option au formulaire ---

  addOption() {
    if (!this.hasOptionsContainerTarget) return

    const container = this.optionsContainerTarget
    const input = document.createElement("input")
    input.type = "text"
    input.name = `${this.paramKeyValue}[options][]`
    input.placeholder = `Option ${container.children.length + 1}`
    input.className = container.children[0].className
    container.appendChild(input)
    input.focus()
  }

  // --- Admin: creation d'un sondage via fetch ---

  async createPoll(event) {
    event.preventDefault()

    const form = event.currentTarget
    const formData = new FormData(form)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: { "X-CSRF-Token": token },
        body: formData
      })

      if (response.ok) {
        form.reset()
        // Retirer les champs d'options supplementaires
        if (this.hasOptionsContainerTarget) {
          const inputs = this.optionsContainerTarget.querySelectorAll("input")
          inputs.forEach((input, i) => {
            if (i >= MIN_OPTIONS) input.remove()
          })
        }
      } else {
        this.flashError(this.hasSubmitBtnTarget ? this.submitBtnTarget : form)
      }
    } catch (error) {
      console.error("[Poll] Create error:", error)
      this.flashError(this.hasSubmitBtnTarget ? this.submitBtnTarget : form)
    }
  }

  // --- Admin: fermeture d'un sondage via fetch ---

  async closePoll(event) {
    event.preventDefault()

    const button = event.currentTarget
    const pollId = parseInt(button.dataset.pollId)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(
        `${this.pollsUrlValue}/${pollId}/close`,
        {
          method: "POST",
          headers: {
            "X-CSRF-Token": token,
            "Accept": "application/json"
          }
        }
      )

      if (!response.ok) {
        this.flashError(button)
      }
    } catch (error) {
      console.error("[Poll] Close error:", error)
      this.flashError(button)
    }
  }

  // --- Admin: suppression d'un sondage via fetch ---

  async deletePoll(event) {
    event.preventDefault()

    if (!window.confirm(this.labelDeleteConfirmValue)) return

    const button = event.currentTarget
    const pollId = parseInt(button.dataset.pollId)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(
        `${this.pollsUrlValue}/${pollId}`,
        {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": token,
            "Accept": "application/json"
          }
        }
      )

      if (!response.ok) {
        this.flashError(button)
      }
    } catch (error) {
      console.error("[Poll] Delete error:", error)
      this.flashError(button)
    }
  }

  // --- Student: vote ---

  async vote(event) {
    event.preventDefault()

    const button = event.currentTarget
    const pollId = parseInt(button.dataset.pollId)
    const optionIndex = button.dataset.optionIndex
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(
        `${this.pollsUrlValue}/${pollId}/vote`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": token,
            "Accept": "application/json"
          },
          body: JSON.stringify({ selected_option: optionIndex })
        }
      )

      if (response.ok) {
        this.showVotedState(pollId, parseInt(optionIndex))
        this.unvotedPollIds.delete(pollId)
        this.updatePollBadge()
      } else {
        this.flashError(button)
      }
    } catch (error) {
      console.error("[Poll] Vote error:", error)
      this.flashError(button)
    }
  }

  // --- Poll badge (student) ---

  updatePollBadge() {
    if (!this.hasPollBadgeTarget) return

    const count = this.unvotedPollIds.size
    if (count > 0) {
      this.pollBadgeTarget.textContent = count > 99 ? "99+" : count
      this.pollBadgeTarget.classList.remove("hidden")
      this.pollBadgeTarget.classList.add("inline-flex")
    } else {
      this.pollBadgeTarget.classList.add("hidden")
      this.pollBadgeTarget.classList.remove("inline-flex")
    }
  }

  // --- Insertion d'un nouveau sondage dans la liste ---

  insertNewPoll(pollId, question, options) {
    if (!this.hasPollListTarget) return

    const html = this.roleValue === "trainer"
      ? this.buildAdminPollCard(pollId, question, options)
      : this.buildStudentPollCard(pollId, question, options)

    this.pollListTarget.insertAdjacentHTML("afterbegin", html)
  }

  // --- Mise a jour des barres de resultats ---

  updatePollResults(pollId, results) {
    results.forEach(result => {
      // Texte du pourcentage
      this.element.querySelectorAll(
        `[data-result-percent][data-poll-id="${pollId}"][data-option-index="${result.index}"]`
      ).forEach(el => {
        el.textContent = this.roleValue === "trainer"
          ? `${result.count} (${result.percentage}%)`
          : `${result.percentage}%`
      })

      // Largeur de la barre
      this.element.querySelectorAll(
        `[data-result-bar][data-poll-id="${pollId}"][data-option-index="${result.index}"]`
      ).forEach(el => {
        el.style.width = `${Math.min(result.percentage, 100)}%`
      })
    })

    // Compteur total de votes
    const totalVotes = results.reduce((sum, r) => sum + r.count, 0)
    this.element.querySelectorAll(
      `[data-vote-count][data-poll-id="${pollId}"]`
    ).forEach(el => {
      el.textContent = totalVotes
    })
  }

  // --- Marquer un sondage comme cloture ---

  markPollClosed(pollId) {
    // Badge de statut
    const badge = this.element.querySelector(
      `[data-status-badge][data-poll-id="${pollId}"]`
    )
    if (badge) {
      if (this.roleValue === "trainer") {
        badge.className = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
        badge.textContent = this.labelClosedValue
      } else {
        badge.className = "text-xs text-slate-500 flex-shrink-0"
        badge.textContent = this.labelClosedValue
      }
    }

    // Retirer le bouton de fermeture (admin)
    const closeAction = this.element.querySelector(
      `[data-close-action][data-poll-id="${pollId}"]`
    )
    if (closeAction) closeAction.remove()

    // Desactiver les boutons de vote (student)
    const pollCard = this.element.querySelector(`#live_session_poll_${pollId}`)
    if (pollCard) {
      pollCard.querySelectorAll('button[data-action*="vote"]').forEach(btn => {
        btn.disabled = true
        btn.classList.add("opacity-50", "cursor-not-allowed")
        btn.classList.remove("hover:bg-slate-600")
      })
    }
  }

  // --- Apres un vote, remplacer les boutons par des barres de resultats ---

  showVotedState(pollId, selectedIndex) {
    const pollCard = this.element.querySelector(`#live_session_poll_${pollId}`)
    if (!pollCard) return

    const optionsArea = pollCard.querySelector("[data-options-area]")
    if (!optionsArea) return

    // querySelectorAll retourne un NodeList statique, outerHTML est safe dans forEach
    const buttons = optionsArea.querySelectorAll('button[data-action*="vote"]')
    buttons.forEach(btn => {
      const optionIndex = parseInt(btn.dataset.optionIndex)
      const optionText = btn.textContent.trim()

      btn.outerHTML = `
        <div class="relative">
          <div class="student-progress-bar h-8 rounded-lg">
            <div class="student-progress-fill h-full rounded-lg"
                 data-result-bar
                 data-poll-id="${pollId}"
                 data-option-index="${optionIndex}">
            </div>
          </div>
          <div class="absolute inset-0 flex items-center justify-between px-3">
            <span class="text-xs text-white font-medium z-10">${this.escapeHtml(optionText)}</span>
            <span class="text-xs text-slate-300 z-10"
                  data-result-percent
                  data-poll-id="${pollId}"
                  data-option-index="${optionIndex}">
              0%
            </span>
          </div>
        </div>
      `
    })
  }

  // --- Templates HTML ---

  buildAdminPollCard(pollId, question, options) {
    const optionsHtml = options.map((option, index) => `
      <div class="flex items-center gap-2">
        <div class="flex-1">
          <div class="flex items-center justify-between text-xs text-gray-600 mb-0.5">
            <span>${this.escapeHtml(option)}</span>
            <span class="font-medium"
                  data-result-percent
                  data-poll-id="${pollId}"
                  data-option-index="${index}">0 (0%)</span>
          </div>
          <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div class="h-full bg-blue-500 rounded-full transition-all duration-500"
                 data-result-bar
                 data-poll-id="${pollId}"
                 data-option-index="${index}"></div>
          </div>
        </div>
      </div>
    `).join("")

    return `
      <div class="border border-gray-200 rounded-lg p-3"
           id="live_session_poll_${pollId}"
           data-live-session-poll-target="pollCard"
           data-poll-id="${pollId}">
        <div class="flex items-start justify-between mb-2">
          <p class="text-sm font-medium text-gray-800">${this.escapeHtml(question)}</p>
          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
                data-status-badge
                data-poll-id="${pollId}">${this.labelActiveValue}</span>
        </div>
        <div class="space-y-1.5">${optionsHtml}</div>
        <div class="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
          <div data-close-action data-poll-id="${pollId}">
            <button type="button"
                    class="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
                    data-action="click->live-session-poll#closePoll"
                    data-poll-id="${pollId}">
              <i class="fas fa-times-circle mr-1"></i>${this.labelCloseValue}
            </button>
          </div>
          <button type="button"
                  class="text-xs text-gray-400 hover:text-red-500 transition-colors"
                  data-action="click->live-session-poll#deletePoll"
                  data-poll-id="${pollId}">
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    `
  }

  buildStudentPollCard(pollId, question, options) {
    const optionsHtml = options.map((option, index) => `
      <button type="button"
              class="w-full text-left bg-slate-700 hover:bg-slate-600 rounded-lg px-3 py-2 text-sm text-white transition-colors duration-200"
              data-action="click->live-session-poll#vote"
              data-poll-id="${pollId}"
              data-option-index="${index}">
        ${this.escapeHtml(option)}
      </button>
    `).join("")

    return `
      <div id="live_session_poll_${pollId}"
           class="bg-slate-800 rounded-lg p-3"
           data-live-session-poll-target="pollCard"
           data-poll-id="${pollId}">
        <div class="flex items-start justify-between gap-2 mb-3">
          <p class="text-sm font-medium text-white">${this.escapeHtml(question)}</p>
          <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0 mt-1.5"
                data-status-badge
                data-poll-id="${pollId}"></span>
        </div>
        <div class="space-y-2" data-options-area>${optionsHtml}</div>
        <p class="text-xs text-slate-500 mt-2">
          <span data-vote-count data-poll-id="${pollId}">0</span>
          ${this.labelVotesValue}
        </p>
      </div>
    `
  }

  // --- Helpers ---

  flashError(element) {
    element.classList.add("ring-2", "ring-red-500")
    setTimeout(() => {
      element.classList.remove("ring-2", "ring-red-500")
    }, ERROR_FEEDBACK_MS)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
